import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUTMs } from "@/lib/utms";

const inputBase =
  "w-full border-b border-gray-400/50 bg-transparent py-3 px-0 focus:outline-none focus:border-teal text-ink placeholder:text-gray-600/70 transition-colors";

const SMS_CONSENT_TEXT =
  "I agree to receive text messages from Jos Family Law about my inquiry, including intake links, appointment reminders, and case updates. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out.";

export default function ConsultationForm({
  variant = "default",
  source,
  eyebrow,
  headline,
  sub,
  ctaLabel,
} = {}) {
  const idPrefix = `cf-${variant}`;
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [smsConsent, setSmsConsent] = useState(false); // unchecked by default; not required to submit
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.message.trim()) e.message = "Please describe your situation.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const utms = getUTMs();
      const payload = { ...form, ...utms };
      if (source) payload.source = source;
      payload.sms_consent = smsConsent;
      payload.sms_consent_text = smsConsent ? SMS_CONSENT_TEXT : "";
      await api.post("/consultations", payload);

      // === DIAGNOSTIC LOGGING — remove after verification ===
      console.log("[JFL Tracking] Form submitted successfully (201)");
      console.log("[JFL Tracking] typeof window.gtag =", typeof window.gtag);
      console.log("[JFL Tracking] typeof window.fbq =", typeof window.fbq);
      console.log("[JFL Tracking] window.dataLayer length:", (window.dataLayer || []).length);
      // ======================================================

      // GA4 lead conversion event — fires for every form submission across all 5 landing pages.
      // View in GA4: Reports → Engagement → Events → generate_lead
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        console.log("[JFL Tracking] Firing GA4 generate_lead");
        window.gtag("event", "generate_lead", {
          campaign_source: source || "Unknown",
          form_variant: variant,
          utm_source: utms.utm_source || "(direct)",
          utm_medium: utms.utm_medium || "(none)",
          utm_campaign: utms.utm_campaign || "(none)",
        });

        // Google Ads conversion — "Consultation Form Submit" action.
        // Only fires after the backend accepts the lead (201 Created above).
        console.log("[JFL Tracking] Firing Google Ads conversion (AW-18245305499/VM24COiUlMYcEJuJhfxD)");
        window.gtag("event", "conversion", {
          send_to: "AW-18245305499/VM24COiUlMYcEJuJhfxD",
        });
      } else {
        console.warn("[JFL Tracking] window.gtag is NOT a function — GA4 + Google Ads events skipped");
      }

      // Meta Pixel lead event — same submission, fires to Meta Events Manager.
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        console.log("[JFL Tracking] Firing Meta Pixel Lead event");
        window.fbq("track", "Lead", {
          content_name: source || "Consultation",
          content_category: variant,
        });
      } else {
        console.warn("[JFL Tracking] window.fbq is NOT a function — Meta Lead event skipped");
      }

      setSubmitted(true);
      setForm({ name: "", phone: "", email: "", message: "" });
      toast.success("Request received. An attorney will reach out shortly.");
    } catch (err) {
      console.error("[JFL Tracking] Submission threw an exception:", err);
      const detail =
        err?.response?.data?.detail ||
        "We couldn't send your request. Please try again or call our office.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const containerCls =
    variant === "hero"
      ? "relative bg-paper border border-gray-400/30 shadow-[0_20px_60px_-20px_rgba(35,31,32,0.18)] p-8 md:p-10"
      : "relative bg-paper border border-gray-400/30 p-8 md:p-10";

  if (submitted) {
    return (
      <div
        data-testid="consultation-form-success"
        className={containerCls + " grain"}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-px bg-teal" />
          <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-teal">
            Request Received
          </p>
        </div>
        <h3 className="font-display text-3xl md:text-4xl leading-tight text-ink">
          Thank you. We&rsquo;ll be in touch{" "}
          <span className="italic">shortly.</span>
        </h3>
        <p className="mt-4 text-gray-800 leading-relaxed">
          A representative from our law firm will contact you by phone and
          email to schedule your confidential consultation. We&rsquo;ve also
          sent a welcome email to your inbox with a quick intake form to help
          us prepare for your call — please check your inbox (and spam folder
          if needed). If your matter is urgent, please call our office
          directly at{" "}
          <a
            href="tel:+17147337066"
            className="text-teal hover:text-ink transition-colors font-medium whitespace-nowrap"
          >
            (714) 733-7066
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          data-testid="consultation-form-reset"
          className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-ink border-b border-ink hover:text-teal hover:border-teal transition-colors"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="consultation-form"
      className={containerCls}
      noValidate
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="w-10 h-px bg-teal" />
        <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-teal">
          {eyebrow || "Speak With An Attorney"}
        </p>
      </div>
      <h3 className="font-display text-[28px] md:text-[34px] leading-tight tracking-tight text-ink">
        {headline || (
          <>
            Tell us about your <span className="italic">situation.</span>
          </>
        )}
      </h3>
      <p className="mt-3 text-sm text-gray-800 leading-relaxed">
        {sub ||
          "We'll reach out promptly to schedule your confidential consultation."}
      </p>

      <div className="mt-8 space-y-6">
        <div>
          <label
            htmlFor={`${idPrefix}-name`}
            className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
          >
            Name <span aria-hidden className="text-teal">*</span>
          </label>
          <input
            id={`${idPrefix}-name`}
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            placeholder="Full name"
            data-testid="consultation-input-name"
            className={inputBase}
            autoComplete="name"
            required
          />
          {errors.name && (
            <p data-testid="consultation-error-name" className="mt-1 text-xs text-red-600">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor={`${idPrefix}-phone`}
            className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
          >
            Phone <span aria-hidden className="text-teal">*</span>
          </label>
          <input
            id={`${idPrefix}-phone`}
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            data-testid="consultation-input-phone"
            className={inputBase}
            autoComplete="tel"
            required
          />
          {errors.phone && (
            <p data-testid="consultation-error-phone" className="mt-1 text-xs text-red-600">
              {errors.phone}
            </p>
          )}

          {/* SMS opt-in — unchecked by default; the form submits with or without it. */}
          <label
            htmlFor={`${idPrefix}-sms-consent`}
            className="mt-4 flex items-start gap-3 cursor-pointer group"
          >
            <input
              id={`${idPrefix}-sms-consent`}
              type="checkbox"
              name="sms_consent"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              data-testid="consultation-input-sms-consent"
              className="mt-[3px] h-4 w-4 shrink-0 accent-teal cursor-pointer"
            />
            <span className="text-[12px] leading-[1.6] text-gray-700 group-hover:text-ink transition-colors">
              I agree to receive text messages from Jos Family Law about my
              inquiry, including intake links, appointment reminders, and case
              updates. Message frequency varies. Msg &amp; data rates may
              apply. Reply <span className="font-mono">STOP</span> to opt out.
              See our{" "}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-teal hover:text-ink underline transition-colors"
                data-testid="consultation-sms-privacy-link"
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-teal hover:text-ink underline transition-colors"
                data-testid="consultation-sms-terms-link"
              >
                Terms of Use
              </Link>
              .
            </span>
          </label>
        </div>

        <div>
          <label
            htmlFor={`${idPrefix}-email`}
            className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
          >
            Email <span aria-hidden className="text-teal">*</span>
          </label>
          <input
            id={`${idPrefix}-email`}
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@example.com"
            data-testid="consultation-input-email"
            className={inputBase}
            autoComplete="email"
            required
          />
          {errors.email && (
            <p data-testid="consultation-error-email" className="mt-1 text-xs text-red-600">
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor={`${idPrefix}-message`}
            className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
          >
            Message <span aria-hidden className="text-teal">*</span>
          </label>
          <textarea
            id={`${idPrefix}-message`}
            name="message"
            rows={4}
            value={form.message}
            onChange={handleChange}
            placeholder="Briefly describe your situation."
            data-testid="consultation-input-message"
            className={
              "w-full border border-gray-400/30 bg-paper p-3 focus:outline-none focus:border-teal text-ink placeholder:text-gray-600/70 transition-colors resize-none"
            }
            required
          />
          {errors.message && (
            <p data-testid="consultation-error-message" className="mt-1 text-xs text-red-600">
              {errors.message}
            </p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="consultation-submit-button"
        className="mt-8 w-full inline-flex items-center justify-center bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-6 py-5 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="font-mono text-[12px] md:text-[13px] tracking-[0.24em] uppercase font-semibold">
          {submitting
            ? "Sending…"
            : ctaLabel || "Schedule Your Confidential Consultation"}
        </span>
      </button>

      {variant === "hero" && null}
    </form>
  );
}
